import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";

import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { pb } from "../../helpers/pocketbase_helper";
import Switch from "react-switch";

// For filtering and pagination
import TableContainer from "../../components/Common/DataTableContainer";

import defaultAvatar from "../../assets/images/users/avatar-6.jpg"; // Usa tu avatar por defecto

const Usuarios = () => {
  document.title = "Usuarios | GraphicForce - Printesco";

  const [users, setUsers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [roles, setRoles] = useState({});
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  
  // Estado de paginación
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isPagination] = useState(true);
  const [isCustomPageSize] = useState(true);

  const [form, setForm] = useState({ nombre: "", email: "", rol: "" });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formActivo, setFormActivo] = useState(true);

  // For delete confirmation
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [modalError, setModalError] = useState(""); // Para errores en el modal

  // Fetch users and roles with pagination
  useEffect(() => {
    async function fetchData() {
      try {
        const rolesList = await pb.collection("rol").getFullList();
        const rolesMap = {};
        rolesList.forEach(r => { rolesMap[r.id] = r.nombre; });
        setRoles(rolesMap);

        const resultList = await pb.collection("users").getList(pageIndex + 1, pageSize, {
          sort: "-created"
        });
        setUsers(resultList.items);
        setTotalRecords(resultList.totalItems);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    }
    fetchData();
  }, [pageIndex, pageSize]);

  // Utility to generate a random password (8 chars, alphanumeric)
  function generateRandomPassword(length = 8) {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pass = "";
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  }

  const getAvatarUrl = (user) => {
    if (user.avatar) {
      return `${process.env.REACT_APP_PB_URL}/api/files/users/${user.id}/${user.avatar}`;
    }
    return defaultAvatar;
  };

  // Reload users only
  const reloadUsers = async () => {
    const resultList = await pb.collection("users").getList(pageIndex + 1, pageSize, {
      sort: "-created"
    });
    setUsers(resultList.items);
    setTotalRecords(resultList.totalItems);
  };

  // Manejar cambio de archivo de avatar (solo archivos < 1MB)
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 1024 * 1024) {
      setModalError("El archivo de avatar debe ser menor a 1MB.");
      e.target.value = "";
      setAvatarFile(null);
      return;
    }
    setModalError(""); // Limpiar error si el archivo es válido
    setAvatarFile(file || null);
  };

  // Limpiar el file input después de guardar
  const resetAvatarInput = () => {
    setAvatarFile(null);
    if (document.getElementById("avatar_input")) {
      document.getElementById("avatar_input").value = "";
    }
    if (document.getElementById("avatar_input_edit")) {
      document.getElementById("avatar_input_edit").value = "";
    }
  };

  // Handlers for add, edit, remove
  const handleAdd = () => {
    setForm({ nombre: "", email: "", rol: "" });
    setAvatarFile(null);
    setModalAdd(true);
  };

  const handleEdit = (user) => {
    setForm({ nombre: user.name, email: user.email, rol: user.rol });
    setFormActivo(user.activo !== false);
    setEditId(user.id);
    setAvatarFile(null);
    setModalEdit(true);
  };

  // Show alert and store user to delete
  const handleRemove = (user) => {
    setUserToDelete(user);
    setShowDeleteAlert(true);
  };

  // Confirm delete: set activo to false (soft delete)
  const confirmRemove = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await pb.collection("users").update(userToDelete.id, { activo: false });
      reloadUsers();
      setShowDeleteAlert(false);
      setUserToDelete(null);
    } catch (err) {
      alert("Error al desactivar usuario");
    }
    setDeleting(false);
  };

  // Cancel delete
  const cancelRemove = () => {
    setShowDeleteAlert(false);
    setUserToDelete(null);
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const randomPassword = generateRandomPassword();
      const data = {
        name: form.nombre,
        email: form.email,
        rol: form.rol,
        password: randomPassword,
        passwordConfirm: randomPassword,
        emailVisibility: true,
        activo: true,
      };
      if (avatarFile) {
        data.avatar = avatarFile;
      }
      await pb.collection("users").create(data);
      reloadUsers();
      setModalAdd(false);
      resetAvatarInput();
    } catch (err) {
      alert("Error al agregar usuario");
    }
    setSaving(false);
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        name: form.nombre,
        email: form.email,
        rol: form.rol,
        activo: formActivo,
      };
      if (avatarFile) {
        data.avatar = avatarFile;
      }
      await pb.collection("users").update(editId, data);
      reloadUsers();
      setModalEdit(false);
      resetAvatarInput();
    } catch (err) {
      alert("Error al editar usuario");
    }
    setSaving(false);
  };

  // --- Filtering and Pagination ---
  const columns = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Avatar",
      accessorKey: "avatar",
      cell: info => {
        const user = info.row.original;
        return (
          <div className="col-md-6" style={{ maxWidth: 100, display: "flex", justifyContent: "center" }}>
            <img
              src={getAvatarUrl(user)}
              alt="avatar"
              className="avatar-sm rounded-circle"
              style={{ objectFit: "cover", width: 40, height: 40 }}
            />
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Rol",
      accessorKey: "rol",
      cell: info => roles[info.getValue()] || "-",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Activo",
      accessorKey: "activo",
      cell: info =>
        info.getValue() === false
          ? <span className="badge bg-danger">No</span>
          : <span className="badge bg-success">Sí</span>,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: info => {
        const user = info.row.original;
        return (
          <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
            <Button color="warning" size="sm" className="me-2" onClick={() => handleEdit(user)}>
              <i className="fas fa-pen me-1"></i> Editar
            </Button>
            <Button color="danger" size="sm" onClick={() => handleRemove(user)} disabled={user.activo === false}>
              <i className="fas fa-eraser me-1"></i> Eliminar
            </Button>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], [roles, users]);



  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs maintitle="Dashboard" title="Administracion" breadcrumbItem="Usuarios" />

          {/* Dismissing Alert for Delete */}
          {showDeleteAlert && userToDelete && (
            <Alert color="warning" isOpen={showDeleteAlert} toggle={cancelRemove} fade={true} transition={{ timeout: 150 }} className="d-flex align-items-center justify-content-between">
              <div>
                <strong>¿Está seguro?</strong> Esto desactivará al usuario <b>{userToDelete.email}</b>.
              </div>
              <div>
                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                  {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                </Button>
                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                  Cancelar
                </Button>
              </div>
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    Usuarios
                    <div>
                      <Button color="primary" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus me-1"></i> Agregar
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        className="ms-2"
                        title="Recargar tabla"
                        onClick={reloadUsers}
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                  </CardTitle>
                  <div className="table-rep-plugin">
                    <div className="table-responsive mb-0" data-pattern="priority-columns">
                      <TableContainer
                        columns={columns}
                        data={users}
                        isGlobalFilter={false}
                        isPagination={true}
                        isCustomPageSize={true}
                        pageIndex={pageIndex}
                        setPageIndex={setPageIndex}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalRecords={totalRecords}
                        pagination="pagination"
                        paginationWrapper='dataTables_paginate paging_simple_numbers'
                        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Large Modal for Add User */}
      <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
        <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Usuario</ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {modalError && (
              <Alert
                color="danger"
                isOpen={!!modalError}
                toggle={() => setModalError("")}
                fade={true}
                transition={{ timeout: 150 }}
                className="d-flex align-items-center justify-content-between mb-3"
              >
                <div>
                  <strong>Error:</strong> {modalError}
                </div>
              </Alert>
            )}
            <FormGroup>
              <Label for="nombre">Nombre</Label>
              <Input
                type="text"
                name="nombre"
                id="nombre"
                value={form.nombre}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="avatar_input">Avatar</Label>
              <Input
                type="file"
                name="avatar"
                id="avatar_input"
                accept="image/*"
                onChange={handleAvatarChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="email">Email</Label>
              <Input
                type="email"
                name="email"
                id="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="rol">Rol</Label>
              <Input
                type="select"
                name="rol"
                id="rol"
                value={form.rol}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione un rol</option>
                {Object.entries(roles).map(([id, nombre]) => (
                  <option key={id} value={id}>{nombre}</option>
                ))}
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalAdd(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Large Modal for Edit User */}
      <Modal isOpen={modalEdit} toggle={() => setModalEdit(!modalEdit)} size="lg">
        <ModalHeader toggle={() => setModalEdit(!modalEdit)}>Editar Usuario</ModalHeader>
        <Form onSubmit={handleUpdate}>
          <ModalBody>
            {modalError && (
              <Alert
                color="danger"
                isOpen={!!modalError}
                toggle={() => setModalError("")}
                fade={true}
                transition={{ timeout: 150 }}
                className="d-flex align-items-center justify-content-between mb-3"
              >
                <div>
                  <strong>Error:</strong> {modalError}
                </div>
              </Alert>
            )}
            <FormGroup>
              <Label for="nombre_edit">Nombre</Label>
              <Input
                type="text"
                name="nombre"
                id="nombre_edit"
                value={form.nombre}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="avatar_input_edit">Avatar</Label>
              <Input
                type="file"
                name="avatar"
                id="avatar_input_edit"
                accept="image/*"
                onChange={handleAvatarChange}
              />
            </FormGroup>
            <FormGroup>
              <Label for="email_edit">Email</Label>
              <Input
                type="email"
                name="email"
                id="email_edit"
                value={form.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="rol_edit">Rol</Label>
              <Input
                type="select"
                name="rol"
                id="rol_edit"
                value={form.rol}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione un rol</option>
                {Object.entries(roles).map(([id, nombre]) => (
                  <option key={id} value={id}>{nombre}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Activo</Label>
              <div>
                <Switch
                  checked={formActivo}
                  onChange={setFormActivo}
                  onColor="#02a499"
                  offColor="#ec4561"
                  uncheckedIcon={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        fontSize: 12,
                        color: "#fff",
                        paddingRight: 2
                      }}
                    >
                      No
                    </div>
                  }
                  checkedIcon={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        fontSize: 12,
                        color: "#fff",
                        paddingRight: 2
                      }}
                    >
                      Sí
                    </div>
                  }
                />
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalEdit(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Usuarios;